import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNotificationBar } from 'context/notificationContext'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'

import { useQueryClient } from 'react-query'
import ErrorBoundary from 'components/ErrorBoundary'
import {
  presignEpisodeFile,
  uploadFileToPresignedUrl,
  completeEpisodeUpload,
  PresignFileType,
} from 'hooks/episode'
import { TextField } from 'components/styledFormElements/TextField'
import { TextareaField } from 'components/styledFormElements/TextareaField'
import { QueryKeys } from 'hooks/_query_keys'
import { getErrorResponse } from 'services/util'
import FileInputUploadFieldNew from 'components/styledFormElements/FineInputUploadNew'
import { useForm, useWatch } from 'react-hook-form'
import VideoDropzone from 'components/styledFormElements/VideoDropzone'
import _, { get, set } from 'lodash'
import LabelsList from 'components/label/LabelList'
import CircularProgress from 'components/styledFormElements/CircularProgress'
import Tooltip from 'components/styledFormElements/Tooltip'
import { LoaderMain } from 'components/loader'
import { duration } from 'moment'
import InputField from 'components/styledFormElements/InputField'
import { FileSizeLimit } from 'constants/file-validation'
import { episodeSchema } from './validation'
import { ConfirmModal } from 'components/confirmationPopup'
import PdfUploadField from 'components/styledFormElements/PdfUploadField'
import { TranscriptUploadField } from 'components/styledFormElements/TranscriptUploadField'
import { useActiveAIPrompt } from 'hooks/ai-settings'
import { useGenerateLabelsFromFile, useGenerateLabelsFromText } from 'hooks/ai-label-generation'
import { SparklesIcon } from '@heroicons/react/24/outline'
interface VideoFileData {
  file: File | null
  duration: any
  size: any
}

const allowedTypes: any = [
  'image/jpeg',
  'image/png',
  'image/jpg',

  // // PDF
  // 'application/pdf',

  // // CSV
  // 'text/csv',
  // 'application/csv',
  // 'application/vnd.ms-excel', // some browsers send CSV as this

  // // Excel
  // 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  // 'application/vnd.ms-excel', // .xls
]

const CreateEpisode: React.FC = () => {
  const notification = useNotificationBar()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const progressRef = React.useRef<NodeJS.Timeout | null>(null)
  const previousEpisodeNameRef = React.useRef<string>('')
  const [open, setOpen] = useState(false)

  const defaultResourceLocation = useMemo(
    () => JSON.stringify([{ timestamp: Date.now(), resource_url: '' }]),
    []
  )

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    control,
    getValues,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      name: '',
      short_summary: '',
      creator: '',
      video_id: '',
      labels: [],
      resourceFile: null,
      thumbnail: null,
      videoFile: null,
      resourceTimestamp: '',
      duration: null,
      transcriptFile: null,
    },
    resolver: yupResolver(episodeSchema),
  })

  const [labelInput, setLabelInput] = useState('')
  const [labelsArray, setLabelsArray] = useState<string[]>([])
  const [videoFile, setVideoFile] = useState<VideoFileData>({
    file: null,
    duration: null,
    size: null,
  })
  const [transcriptFile, setTranscriptFile] = useState<File | string | null>(null)
  const [isGeneratingLabels, setIsGeneratingLabels] = useState(false)

  const [resourceError, setResourceError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number>(0)

  const [isLoading, setIsLoading] = useState(false)
  const { data: activePrompt } = useActiveAIPrompt()
  const generateLabelsFromFile = useGenerateLabelsFromFile()
  const generateLabelsFromText = useGenerateLabelsFromText()

  const resourceTimestamp = useWatch({ control, name: 'resourceTimestamp' })
  const watchedThumbnail = useWatch({ control, name: 'thumbnail' })
  const watchedResourceFile = useWatch({ control, name: 'resourceFile' })
  const watchedVideoFile = useWatch({ control, name: 'videoFile' })

  const watchedName = useWatch({ control, name: 'name' })
  const watchedShortSummary = useWatch({ control, name: 'short_summary' })
  const getUploadPhase = (
    progress: number
  ): 'idle' | 'uploading' | 'processing' => {
    if (progress === 0) return 'idle'
    if (progress <= 20) return 'uploading'
    return 'processing'
  }

  const getUploadLabel = (progress: number) => {
    if (progress <= 20) return 'Uploading video…'
    if (progress <= 40) return 'Transcoding video…'
    return 'Processing video…'
  }
  const uploadPhase = getUploadPhase(uploadProgress)
  /**
   * Thumbnail preview effect
   */

  useEffect(() => {
    if (!(watchedVideoFile instanceof File)) {
      setValue('resourceFile', '');
      setValue('resourceTimestamp', '');
    }
  }, [watchedVideoFile]);

  const thumbUrlRef = React.useRef<string | null>(null)
  const resourceUrlRef = React.useRef<string | null>(null)

  const thumbPreview = useMemo(() => {
    if (!watchedThumbnail) return null

    if (watchedThumbnail instanceof File) {
      if (thumbUrlRef.current) {
        URL.revokeObjectURL(thumbUrlRef.current)
      }
      const url = URL.createObjectURL(watchedThumbnail)
      thumbUrlRef.current = url
      return url
    }

    return watchedThumbnail
  }, [watchedThumbnail])

  const resourcePreview = useMemo(() => {
    if (!watchedResourceFile) return null

    if (watchedResourceFile instanceof File) {
      if (resourceUrlRef.current) {
        URL.revokeObjectURL(resourceUrlRef.current)
      }
      const url = URL.createObjectURL(watchedResourceFile)
      resourceUrlRef.current = url
      return url
    }

    return watchedResourceFile
  }, [watchedResourceFile])

  const normalizeLabel = (label: string) => {
    const cleaned = label.trim()
    if (!cleaned) return ''

    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase()
  }

  /**
   * Submit handler
   */
  const onSubmit = useCallback(
    async (values: any) => {
      if (!labelsArray.length) {
        notification.push({
          title: 'Validation error',
          type: 'error',
          message: 'At least one label is required',
        })
        return
      }

      const stopFakeProgress = () => {
        if (progressRef.current) {
          clearInterval(progressRef.current)
          progressRef.current = null
          setUploadProgress(0) // Reset progress when stopping
        }
      }

      const onSuccess = () => {
        stopFakeProgress()
        setUploadProgress(100)

        notification.push({
          title: 'Episode Added',
          type: 'success',
          message: 'Episode created successfully',
        })

        navigate(-1)
      }

      const onError = (errorRes: any) => {
        stopFakeProgress()
        setUploadProgress(0)
        notification.push(getErrorResponse(errorRes))
      }

      // Every media asset is uploaded straight to S3 via a presigned url, one
      // at a time, in this exact order — video goes last, and only once its
      // S3 upload finishes do we call complete-upload to persist the episode.
      const filesToUpload: { type: PresignFileType; file: File }[] = []
      if (values.thumbnail instanceof File)
        filesToUpload.push({ type: 'thumbnail', file: values.thumbnail })
      if (values.transcriptFile instanceof File)
        filesToUpload.push({ type: 'transcript', file: values.transcriptFile })
      if (values.resourceFile instanceof File)
        filesToUpload.push({ type: 'resource', file: values.resourceFile })
      if (values.videoFile instanceof File)
        filesToUpload.push({ type: 'video', file: values.videoFile })

      setIsLoading(true)

      try {
        const totalBytes = filesToUpload.reduce(
          (sum, { file }) => sum + file.size,
          0
        )
        let bytesDoneBeforeCurrent = 0
        const keyByType: Partial<Record<PresignFileType, string>> = {}
        let videoAsset: { entryId: string; key: string } | null = null

        for (const { type, file } of filesToUpload) {
          const asset = await presignEpisodeFile(file.name, file.type, type)
          const bytesBefore = bytesDoneBeforeCurrent

          await uploadFileToPresignedUrl(asset.uploadUrl, file, (e) => {
            if (!e.total) return
            const loaded = bytesBefore + e.loaded
            const realPercent = totalBytes ? (loaded / totalBytes) * 100 : 100
            setUploadProgress(Math.min(20, Math.round(realPercent * 0.2)))
          })

          bytesDoneBeforeCurrent += file.size
          keyByType[type] = asset.key
          if (type === 'video') videoAsset = asset
        }

        // Single complete-upload call, once every asset is on S3 — carries
        // the video's entryId/key plus the other assets' keys and metadata.
        const completePayload: any = {
          entryId: videoAsset?.entryId,
          key: videoAsset?.key,
          name: values.name,
          short_summary: values.short_summary,
          creator: values.creator,
          labels: labelsArray,
        }

        if (keyByType.thumbnail) completePayload.thumbnailKey = keyByType.thumbnail
        if (keyByType.transcript) completePayload.transcriptKey = keyByType.transcript
        if (keyByType.resource) completePayload.resourceKey = keyByType.resource
        if (values.duration) completePayload.duration = values.duration
        if (values.resourceTimestamp)
          completePayload.resourceTimestamp = values.resourceTimestamp

        await completeEpisodeUpload(completePayload)

        setIsLoading(false)
        onSuccess()
      } catch (err) {
        setIsLoading(false)
        onError(err)
      }
    },
    [labelsArray, notification, navigate]
  )

  useEffect(() => {
    if (uploadProgress === 20 && !progressRef.current) {
      progressRef.current = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) {
            // Clean up interval when we reach the max
            if (progressRef.current) {
              clearInterval(progressRef.current);
              progressRef.current = null;
            }
            return prev;
          }
          return prev + 1;
        });
      }, 300);
    }

    return () => {
      // Cleanup interval if component updates and uploadProgress changes
      if (progressRef.current && uploadProgress >= 95) {
        clearInterval(progressRef.current);
        progressRef.current = null;
      }
    };
  }, [uploadProgress])

  useEffect(() => {
    if (!watchedResourceFile && resourceTimestamp !== '') {
      setResourceError('Resource file is required')
      return
    }

    if (
      videoFile?.duration &&
      resourceTimestamp !== '' &&
      Number(resourceTimestamp) >= Number(videoFile.duration)
    ) {
      setResourceError(
        `Timestamp must be less than ${Math.floor(videoFile.duration)}s`
      )
      return
    }

    setResourceError(null)
  }, [watchedResourceFile, resourceTimestamp, videoFile?.duration])


  const handleCancel = useCallback(() => setOpen(true), [])

  const handleRemoveLabel = useCallback(
    (idx: number) => {
      const labelToRemove = labelsArray[idx]
      const episodeName = watchedName?.trim()

      // Prevent removing the episode name label
      if (episodeName && normalizeLabel(episodeName) === labelToRemove) {
        notification.push({
          title: 'Info',
          type: 'info',
          message: 'Episode name label cannot be removed'
        })
        return
      }

      setLabelsArray((prev) => {
        const next = prev.filter((_, i) => i !== idx)
        setValue('labels', next, { shouldValidate: true })
        return next
      })
    },
    [setValue, labelsArray, watchedName, notification]
  )

  const handleLabelAdd = () => {
    const token = labelInput.trim()
    if (!token) return

    const formatted = normalizeLabel(token)
    const current = getValues('labels')

    if (current.includes(formatted)) return

    const next = [...current, formatted]
    setLabelsArray(next)
    setValue('labels', next, { shouldValidate: true })
    setLabelInput('')
  }

  const handleGenerateLabels = async () => {
    const title = getValues('name')
    const description = getValues('short_summary')

    if (!title.trim()) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: 'Episode title is required for AI label generation'
      })
      return
    }

    if (!description || !description.trim()) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: 'Short summary is required for AI label generation'
      })
      return
    }

    if (description.length > 75) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: 'Short Summary cannot exceed 75 characters'
      })
      return
    }

    setIsGeneratingLabels(true)

    try {
      if (!activePrompt) {
        notification.push({
          title: 'Error',
          type: 'error',
          message: 'No active AI prompt found. Please configure one in Settings.'
        })
        return
      }

      let result
      const promptText = activePrompt.prompt

      // Use transcript file if available, otherwise use text-based generation
      if (transcriptFile && transcriptFile instanceof File) {
        result = await generateLabelsFromFile.mutateAsync({
          file: transcriptFile,
          title,
          description,
          prompt_text: promptText
        })
      } else {
        // Fallback to text-based generation when no transcript is provided
        result = await generateLabelsFromText.mutateAsync({
          title,
          description,
          prompt_text: promptText
        })
      }

      const labels = result?.data?.results?.labels || result?.results?.labels || result?.labels

      if (labels && Array.isArray(labels)) {
        const normalizedLabels = labels
          .filter((label: string) => label && label.trim())
          .map((label: string) => normalizeLabel(label))
          .filter((label: string, index: number, self: string[]) =>
            self.indexOf(label) === index // Remove duplicates
          )

        // Preserve episode name label
        const episodeName = watchedName?.trim()
        const episodeNameLabel = episodeName ? normalizeLabel(episodeName) : ''

        // Remove episode name from AI labels if it exists (to avoid duplicate) - case insensitive
        const aiLabelsWithoutEpisodeName = normalizedLabels.filter(label => 
          label.toLowerCase() !== episodeNameLabel.toLowerCase()
        )

        // Combine: episode name first, then AI labels
        const finalLabels = episodeNameLabel
          ? [episodeNameLabel, ...aiLabelsWithoutEpisodeName]
          : aiLabelsWithoutEpisodeName

        setLabelsArray(finalLabels)
        setValue('labels', finalLabels, { shouldValidate: true })

        notification.push({
          title: 'Success',
          type: 'success',
          message: result?.data?.message || 'Labels generated successfully'
        })
      }
    } catch (error: any) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: error?.response?.data?.message ?? 'Failed to generate labels. Please try again.'
      })
    } finally {
      setIsGeneratingLabels(false)
    }
  }

  // Auto-add episode name as label
  useEffect(() => {
    const episodeName = watchedName?.trim()
    const normalizedName = episodeName ? normalizeLabel(episodeName) : ''
    const previousNormalizedName = previousEpisodeNameRef.current

    // Only update if the normalized name has actually changed
    if (normalizedName !== previousNormalizedName) {
      const currentLabels = getValues('labels') || []

      // Remove the old episode name label if it exists
      const withoutOldName = previousNormalizedName
        ? currentLabels.filter(label => label !== previousNormalizedName)
        : currentLabels

      // Add the new episode name label at the beginning (if not empty and not already present)
      const updatedLabels = normalizedName
        ? (withoutOldName.includes(normalizedName)
            ? withoutOldName
            : [normalizedName, ...withoutOldName])
        : withoutOldName

      setLabelsArray(updatedLabels)
      setValue('labels', updatedLabels, { shouldValidate: true })

      // Update the ref to track the current episode name
      previousEpisodeNameRef.current = normalizedName
    }
  }, [watchedName, setValue, getValues])

  // Cleanup function for intervals and object URLs
  useEffect(() => {
    return () => {
      // Clear any active intervals
      if (progressRef.current) {
        clearInterval(progressRef.current);
        progressRef.current = null;
      }

      // Cleanup object URLs
      if (thumbUrlRef.current) {
        URL.revokeObjectURL(thumbUrlRef.current);
        thumbUrlRef.current = null;
      }

      if (resourceUrlRef.current) {
        URL.revokeObjectURL(resourceUrlRef.current);
        resourceUrlRef.current = null;
      }
    };
  }, [])

  return (
    <div className="w-full px-6 py-6 bg-gray-50 dark:bg-slate-900">

      <ConfirmModal
        open={open}
        title="Are you sure?"
        message="This will remove all the data you have entered. This action cannot be undone."
        confirmText="Confirm"
        cancelText="Cancel"
        variant="danger"
        onCancel={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false)
          navigate(-1)
        }}
      />

      {/* ===================== GLOBAL LOADER ===================== */}
      {isLoading && uploadPhase === 'idle' && (
        <LoaderMain size="md" text="Please wait..." fullscreen />
      )}

      {/* ===================== UPLOAD PROGRESS OVERLAY ===================== */}
      {uploadPhase !== 'idle' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-8 flex flex-col items-center gap-4 shadow-lg w-72">
            <CircularProgress percent={uploadProgress} />
            <p className="text-sm text-center">
              {getUploadLabel(uploadProgress)} ({uploadProgress}%)
            </p>
          </div>
        </div>
      )}

      {/* ===================== MAIN FORM ===================== */}
      {uploadPhase === 'idle' && (
        <>

          {/* ---------- HEADER ---------- */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              Create Episode
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage episode details, media and metadata
            </p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-8"
          >
            {/* ===================================================== */}
            {/* BASIC INFORMATION */}
            {/* ===================================================== */}
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                Basic Information
              </h4>

              {/* Episode Name + Creator */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Episode Name"
                  required
                  helpText="Title of the episode displayed to viewers."
                  placeholder="Enter Episode Name"
                  type="text"
                  register={register('name', { required: 'Episode name is required' })}
                  error={errors?.name?.message as string | ''}
                />

                <InputField
                  label="Creator"
                  required
                  helpText="Name of the creator or uploader."
                  placeholder="Enter Creator Name"
                  type="text"
                  register={register('creator', { required: 'Creator is required' })}
                  error={errors?.creator?.message as string | ''}
                />
              </div>

              {/* Short Summary */}
              <div className="mt-4">
                <InputField
                  label="Short Summary"
                  helpText="Brief description shown in listings and search results."
                  placeholder="Enter Short Summary"
                  type="text"
                  register={register('short_summary')}
                  error={errors?.short_summary?.message as string | ''}
                />
              </div>

              {/* Labels */}
              <div className="mt-4 flex flex-col space-y-1">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Labels
                  </label>
                  <span className="text-red-700">*</span>
                  <Tooltip
                    items={[
                      'Tags to categorize episodes (e.g., Action, Drama)',
                      'Helps in search and recommendations',
                    ]}
                  />
                </div>

                <div className="flex gap-2">
                  <input
                    value={labelInput}
                    // {...register('labels')}
                    onChange={(e) => setLabelInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleLabelAdd()
                      }
                    }}
                    className="flex-1 rounded px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-sm"
                    placeholder="Type label and press Enter"
                    disabled={isGeneratingLabels}
                  />

                  <button
                    type="button"
                    title={!watchedName?.trim() ? "Episode name is required" : !watchedShortSummary?.trim() ? "Short summary is required" : transcriptFile && !(transcriptFile instanceof File) ? "AI generation only works with newly uploaded transcript files" : "Generate labels using AI"}
                    onClick={handleGenerateLabels}
                    disabled={isGeneratingLabels || !watchedName?.trim() || !watchedShortSummary?.trim() || (transcriptFile != null && !(transcriptFile instanceof File))}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                  >
                    <SparklesIcon className="h-4 w-4" />
                    {isGeneratingLabels ? 'Generating...' : 'AI Generate'}
                  </button>
                </div>

                <div className="border border-gray-300 dark:border-gray-600 rounded p-2 max-h-40 overflow-y-auto bg-gray-50 dark:bg-slate-800">
                  {labelsArray.length ? (
                    <LabelsList
                      labels={labelsArray}
                      onRemove={handleRemoveLabel}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">
                      No labels added
                    </span>
                  )}
                </div>
                {errors?.labels && (
                  <span className="text-xs text-red-500">
                    {errors.labels.message as string | ''}
                  </span>
                )}
              </div>

              {/* Transcript Upload */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Transcript
                  </label>
                  <Tooltip
                    items={[
                      'Upload a transcript file (PDF or Word document)',
                      'AI will use this to generate more accurate labels',
                    ]}
                  />
                </div>

                <TranscriptUploadField
                  value={transcriptFile}
                  onChange={(file) => {
                    setTranscriptFile(file)
                    setValue('transcriptFile', file, { shouldValidate: true })
                  }}
                  disabled={isGeneratingLabels}
                  error={errors?.transcriptFile?.message as string}
                />

                {transcriptFile && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    ✓ Transcript uploaded. AI will use this for label generation.
                  </p>
                )}
              </div>
            </div>

            {/* ===================================================== */}
            {/* MEDIA + LINKED RESOURCE */}
            {/* ===================================================== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* ---------------- MEDIA ASSETS ---------------- */}
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Media Assets
                </h4>

                <div className="grid grid-cols-1 gap-6">
                  <FileInputUploadFieldNew
                    label="Thumbnail"
                    name="thumbnail"
                    control={control}
                    errors={errors}
                    preview={thumbPreview}
                    rules={{ required: 'Thumbnail is required' }}
                    required
                    allowedTypes={FileSizeLimit?.thumbnail?.allowedTypes}
                    tooltip={FileSizeLimit?.thumbnail?.tooltipItems as any}
                    fileSizeLimit={FileSizeLimit?.thumbnail}
                  />

                  <VideoDropzone
                    label="Episode Video"
                    name="videoFile"
                    control={control}
                    setError={setError}
                    clearErrors={clearErrors}
                    rules={{ required: 'Video file is required' }}
                    onVideoMeta={(meta) => {
                      setVideoFile(meta)
                      setValue('duration', Math.floor(meta?.duration || 0), {
                        shouldValidate: true,
                      })
                    }}
                    required
                    tooltip={FileSizeLimit?.video?.tooltipItems as any}
                    fileSizeLimit={{
                      max_size: FileSizeLimit.video.max_size,
                      message: FileSizeLimit.video.message,
                    }}
                  />


                  {errors.videoFile && (
                    <p className="text-xs text-red-500 mt-1">
                      {String(errors.videoFile.message)}
                    </p>
                  )}
                </div>
              </div>

              {/* ---------------- LINKED RESOURCE ---------------- */}
              <div className="bg-white dark:bg-slate-900 border border-dashed border-gray-300 dark:border-slate-700 rounded-lg p-6">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                  📦 Linked Resource
                </h4>
                <p className="text-xs text-gray-500 mb-4">
                  Attach resource linked to a video timestamp
                </p>

                {!videoFile?.file && (
                  <p className="text-sm text-red-500">
                    Select a video to enable resources
                  </p>
                )}
                {videoFile?.file && (
                  <PdfUploadField
                    label="Resource File"
                    name="resourceFile"
                    tooltip={{
                      title: 'Resource File',
                      position: 'top',
                      items: [
                        'Resource: Upload the resource file that will be made available to users.',
                        'Timestamp: Enter the timestamp.',
                      ]
                    }}
                    control={control}
                    errors={errors}
                  />
                )}

                {/* {videoFile?.file && (
        <FileInputUploadFieldNew
          label="Resource File"
          name="resourceFile"
          control={control}
          errors={errors}
          preview={resourcePreview}
        />
      )} */}

                <input
                  type="number"
                  min={0}
                  disabled={!videoFile?.file}
                  value={resourceTimestamp}
                  onChange={(e) =>
                    setValue('resourceTimestamp', Number(e.target.value))
                  }
                  placeholder="Timestamp (seconds)"
                  className="w-full rounded-md border px-3 py-2 text-sm mt-2 dark:bg-slate-700 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />

                {resourceError && (
                  <p className="text-sm text-red-500 mt-1">
                    {resourceError}
                  </p>
                )}
              </div>
            </div>

            {/* ===================================================== */}
            {/* STICKY ACTION BAR */}
            {/* ===================================================== */}
            <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex justify-end gap-3 z-10">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-300 dark:bg-slate-700 rounded"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>

        </>
      )}
    </div>

  )
}

export default CreateEpisode;
